import { SeriesData, SeriesMetadata } from '../types';
import AuthService from './authService';

class DataService {
  private static instance: DataService;
  private authService: AuthService;
  private baseUrl: string;

  private constructor() {
    this.authService = AuthService.getInstance();
    // Use the publish API base URL as the base for all endpoints
    this.baseUrl = process.env.REACT_APP_PUBLISH_API_URL?.replace('/publish', '') || 
                   'https://a3eaz3ci36.execute-api.us-west-2.amazonaws.com/prod';
  }

  public static getInstance(): DataService {
    if (!DataService.instance) {
      DataService.instance = new DataService();
    }
    return DataService.instance;
  }

  async getHeaders(): Promise<Record<string, string>> {
    const userId = this.authService.getCurrentUserId();
    const token = await this.authService.getIdToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-User-ID': userId || '',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const targetUserId = sessionStorage.getItem('admin_target_user_id');
    if (targetUserId && this.authService.isAdmin()) {
      headers['X-Target-User-ID'] = targetUserId;
    }
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // Fallback to status text if JSON parsing fails
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    return await response.json();
  }

  async listUserSeries(): Promise<SeriesMetadata[]> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch(`${this.baseUrl}/data/series`, {
        method: 'GET',
        headers: await this.getHeaders(),
      });

      return await this.handleResponse<SeriesMetadata[]>(response);
    } catch (error) {
      console.error('Failed to list user series:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to load series list');
    }
  }

  async loadSeries(seriesId: string): Promise<SeriesData> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch(`${this.baseUrl}/data/series/${encodeURIComponent(seriesId)}`, {
        method: 'GET',
        headers: await this.getHeaders(),
      });

      return await this.handleResponse<SeriesData>(response);
    } catch (error) {
      console.error('Failed to load series:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to load series');
    }
  }

  async saveSeries(seriesData: SeriesData): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Update metadata
    const now = new Date().toISOString();
    if (!seriesData.metadata.createdAt) {
      seriesData.metadata.createdAt = now;
    }
    seriesData.metadata.lastModified = now;
    seriesData.metadata.participantCount = seriesData.participants?.length || 0;
    
    // Count races from participant data
    const races = new Set<string>();
    seriesData.participants?.forEach(participant => {
      if (participant.races && typeof participant.races === 'object') {
        Object.keys(participant.races).forEach(raceId => races.add(raceId));
      }
    });
    seriesData.metadata.raceCount = races.size;

    try {
      const response = await fetch(`${this.baseUrl}/data/series`, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(seriesData),
      });

      await this.handleResponse<{ success: boolean; seriesId: string }>(response);
    } catch (error) {
      console.error('Failed to save series:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to save series');
    }
  }

  async updateSeries(seriesId: string, seriesData: SeriesData): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Ensure the series ID matches
    seriesData.metadata.id = seriesId;
    
    // Update metadata (same as saveSeries)
    const now = new Date().toISOString();
    seriesData.metadata.lastModified = now;
    seriesData.metadata.participantCount = seriesData.participants?.length || 0;
    
    const races = new Set<string>();
    seriesData.participants?.forEach(participant => {
      if (participant.races && typeof participant.races === 'object') {
        Object.keys(participant.races).forEach(raceId => races.add(raceId));
      }
    });
    seriesData.metadata.raceCount = races.size;

    try {
      const response = await fetch(`${this.baseUrl}/data/series/${encodeURIComponent(seriesId)}`, {
        method: 'PUT',
        headers: await this.getHeaders(),
        body: JSON.stringify(seriesData),
      });

      await this.handleResponse<{ success: boolean; seriesId: string }>(response);
    } catch (error) {
      console.error('Failed to update series:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update series');
    }
  }

  async deleteSeries(seriesId: string): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch(`${this.baseUrl}/data/series/${encodeURIComponent(seriesId)}`, {
        method: 'DELETE',
        headers: await this.getHeaders(),
      });

      await this.handleResponse<{ success: boolean }>(response);
    } catch (error) {
      console.error('Failed to delete series:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete series');
    }
  }

  // Utility method to generate a unique series ID
  generateSeriesId(seriesName: string): string {
    const cleanName = seriesName.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 30);
    
    const timestamp = Date.now().toString(36);
    return `${cleanName}-${timestamp}`;
  }

  // Create a new series with minimal metadata
  createNewSeries(seriesName: string, pointsScheme: string): SeriesData {
    const now = new Date().toISOString();
    const seriesId = this.generateSeriesId(seriesName);

    return {
      metadata: {
        id: seriesId,
        name: seriesName,
        createdAt: now,
        lastModified: now,
        participantCount: 0,
        raceCount: 0,
        pointsScheme,
      },
      participants: [],
      pointsScheme,
      publishingSettings: {
        selectedFields: ['name', 'category', 'totalPoints'],
        selectedRaces: [],
        isPublished: false,
      },
    };
  }

  // Create series metadata from basic info
  createSeriesMetadata(name: string, participantCount: number = 0, raceCount: number = 0): SeriesMetadata {
    const now = new Date().toISOString();
    return {
      id: this.generateSeriesId(name),
      name,
      createdAt: now,
      lastModified: now,
      participantCount,
      raceCount,
    };
  }

  // Update series metadata
  updateSeriesMetadata(metadata: SeriesMetadata, updates: Partial<SeriesMetadata>): SeriesMetadata {
    return {
      ...metadata,
      ...updates,
      lastModified: new Date().toISOString(),
    };
  }

  // Check if series name exists (for collision detection)
  async checkSeriesNameExists(name: string, excludeId?: string): Promise<boolean> {
    try {
      const series = await this.listUserSeries();
      return series.some(s => s.name.toLowerCase() === name.toLowerCase() && s.id !== excludeId);
    } catch (error) {
      console.error('Failed to check series name:', error);
      return false;
    }
  }

  async publishResults(payload: object): Promise<{ success: boolean; url: string }> {
    const url = process.env.REACT_APP_PUBLISH_API_URL ||
      'https://a3eaz3ci36.execute-api.us-west-2.amazonaws.com/prod/publish';
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(payload),
    });
    return this.handleResponse<{ success: boolean; url: string }>(response);
  }

  async listAdminUsers(): Promise<{ userId: string; email: string | null }[]> {
    const response = await fetch(`${this.baseUrl}/data/users`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });
    const data = await this.handleResponse<{ users: { userId: string; email: string | null }[] }>(response);
    return data.users || [];
  }

  setTargetUserId(userId: string | null): void {
    if (userId) sessionStorage.setItem('admin_target_user_id', userId.toUpperCase());
    else sessionStorage.removeItem('admin_target_user_id');
  }

  getTargetUserId(): string | null {
    return sessionStorage.getItem('admin_target_user_id');
  }
  getPublishedResultsUrl(seriesName: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Clean series name for URL
    const cleanName = seriesName.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    
    return `https://seriespoints.zone4.xyz/results/${userId}/${cleanName}.html`;
  }
}

export default DataService; 